/**
 * 번역 탭의 행 목록 — 문항에서 번역할 수 있는 문자열을 트리 순서대로 평평하게 뽑는다.
 *
 * 저장 계약은 두 갈래다 (백엔드 POST /api/translate/subs_init/<sub_code>, 구 화면과 동일):
 *   meta     : sub_name_e/c · sub_description_e/c · sub_info_e/c · sub_data_header  (translation_part=meta)
 *   contents : sub_data_contents 통째로 — 사이드카(_ea/_ca)가 안에 들어 있다      (translation_part=sub_data_contents)
 * 트리 쪽 행은 노드의 i18n 객체를 직접 가리키므로, 편집하면 toV1 이 그대로 실어 나간다.
 * 배열은 배열로 남는다 — 구 화면의 join/split 붕괴는 구조적으로 생길 수 없다.
 */
import {CHOICE_PARENTS} from './structure-tree.js'

export const LOCALES = [
    {key: 'ea', label: 'English', metaSuffix: '_e'},
    {key: 'ca', label: '中文', metaSuffix: '_c'}
]

/** 노드의 i18n 객체 필드를 가리키는 행 */
const nodeRow = (node, field, kind, ko, where) => ({
    id: `${node.id}:${field}`, kind, ko: ko == null ? '' : String(ko), where, scope: 'contents',
    get: (loc) => (node[field] && node[field][loc] !== undefined ? node[field][loc] : ''),
    set: (loc, v, $set) => {
        const cur = node[field] && typeof node[field] === 'object' ? {...node[field]} : {}
        if (v === '' || v == null) delete cur[loc]; else cur[loc] = v
        const next = Object.keys(cur).length ? cur : undefined
        if ($set) $set(node, field, next); else node[field] = next
    }
})

/** sub 메타 필드를 가리키는 행 — GET /subs/init 은 em??ea 를 sub_name_e 로 합쳐 내려준다 */
const metaRow = (sub, base, kind, ko) => ({
    id: `meta:${base}`, kind, ko: ko == null ? '' : String(ko), where: '문항', scope: 'meta',
    get: (loc) => { const k = base + LOCALES.find((l) => l.key === loc).metaSuffix; return sub[k] == null ? '' : sub[k] },
    set: (loc, v, $set) => { const k = base + LOCALES.find((l) => l.key === loc).metaSuffix; if ($set) $set(sub, k, v); else sub[k] = v }
})

/** sub_data_header 안의 키 (enable_na_label 등) — meta 파트로 저장된다 */
const headerRow = (header, base, kind, ko) => ({
    id: `header:${base}`, kind, ko: ko == null ? '' : String(ko), where: '문항 설정', scope: 'meta',
    get: (loc) => { const k = base + (loc === 'ea' ? '_ea' : '_ca'); return header[k] == null ? '' : header[k] },
    set: (loc, v, $set) => { const k = base + (loc === 'ea' ? '_ea' : '_ca'); if ($set) $set(header, k, v); else header[k] = v }
})

/**
 * @param root  fromV1 의 루트 노드
 * @param sub   불러온 sub 객체 (sub_name, sub_name_e, … sub_data_header)
 */
export const i18nRowsOf = (root, sub) => {
    const rows = []
    const s = sub || {}
    // ── 문항 메타 ──
    if (s.sub_name) rows.push(metaRow(s, 'sub_name', '문항 제목', s.sub_name))
    if (s.sub_description) rows.push(metaRow(s, 'sub_description', '문항 설명', s.sub_description))
    if (s.sub_info) rows.push(metaRow(s, 'sub_info', '문항 안내', s.sub_info))
    const header = s.sub_data_header
    if (header && typeof header === 'object' && header.enable_na_label) {
        rows.push(headerRow(header, 'enable_na_label', '해당없음 라벨', header.enable_na_label))
    }
    if (!root) return rows

    // ── 트리 ──
    const table = (node, where) => {
        // 선택지 안 표의 제목 (DFElement 가 table_title_ea/_ca 로 그린다)
        if (!node.root && node.label) rows.push(nodeRow(node, 'i18n', '표 제목', node.label, where))
        ;(node.columns || []).forEach((col) => { if (col.label) rows.push(nodeRow(col, 'i18n', '표 머리글', col.label, where)) })
        ;(node.children || []).forEach((row, r) => {
            ;(row.children || []).forEach((cell, c) => {
                const content = cell.children && cell.children[0]
                const col = (node.columns || [])[c]
                if (!content || !col) return
                const w = `${where} ${r + 1}행 「${col.label}」`
                if (content.type === 'plain_text') { if (content.label) rows.push(nodeRow(content, 'i18n', '표시 칸', content.label, w)) }
                else if (content.type === 'dropdown') {
                    ;(content.children || []).forEach((ch) => { if (ch.label) rows.push(nodeRow(ch, 'i18n', '드롭다운 선택지', ch.label, w)) })
                } else if (content.placeholder) rows.push(nodeRow(content, 'placeholderI18n', '입력 안내', content.placeholder, w))
            })
        })
    }
    const element = (el, where) => {
        if (el.type === 'table') return table(el, `${where} 표`)
        if (el.type === 'input_text' || el.type === 'input_number') {
            if (el.formTitle) rows.push(nodeRow(el, 'formTitleI18n', '입력폼 제목', el.formTitle, where))
            if (el.label) rows.push(nodeRow(el, 'i18n', '입력 앞 문구', el.label, where))
            if (el.placeholder) rows.push(nodeRow(el, 'placeholderI18n', '입력 안내', el.placeholder, where))
            if (el.afterText) rows.push(nodeRow(el, 'afterTextI18n', '입력 뒤 문구', el.afterText, where))
            return
        }
        if (CHOICE_PARENTS.includes(el.type)) return choices(el, where)
        if (el.label) rows.push(nodeRow(el, 'i18n', el.type, el.label, where))
    }
    const choices = (node, where) => {
        ;(node.children || []).forEach((ch, i) => {
            const w = `${where}${where ? ' ' : ''}${i + 1}번 선택지`
            if (ch.label) {
                const r = nodeRow(ch, 'i18n', '선택지', ch.label, w)
                // 저장 키가 문구와 다르면 함께 보여준다 — 채점식이 가리키는 값이라 헷갈리면 안 된다
                if (ch.value !== undefined && ch.value !== ch.label) r.key = ch.value
                rows.push(r)
            }
            if (ch.detail && ch.detailPlaceholder) {
                rows.push(nodeRow(ch, 'detailPlaceholderI18n', '상세 입력 안내', ch.detailPlaceholder, w))
            }
            ;(ch.children || []).forEach((el) => element(el, `${w} 안`))
        })
    }

    const t = root.subType || root.type
    if (t === 'table') table(root, '표')
    else if (CHOICE_PARENTS.includes(t)) choices(root, '')
    else if (t === 'guide_text' || t === 'guide_text2') { if (root.label) rows.push(nodeRow(root, 'i18n', '안내문', root.label, '문항')) }
    else if (t === 'input_text' || t === 'input_number') {
        if (root.placeholder) rows.push(nodeRow(root, 'placeholderI18n', '입력 안내', root.placeholder, '문항'))
        if (root.afterText) rows.push(nodeRow(root, 'afterTextI18n', '입력 뒤 문구', root.afterText, '문항'))
    } else if (t === 'textbox') { if (root.placeholder) rows.push(nodeRow(root, 'placeholderI18n', '입력 안내', root.placeholder, '문항')) }
    return rows
}

/** 빈 번역 개수 — 탭 배지용 */
export const missingCount = (rows) => rows.reduce((n, r) => n + LOCALES.filter((l) => !r.get(l.key)).length, 0)

/** translation_part=meta 로 보낼 sub 조각 */
export const metaPayloadOf = (sub) => {
    const s = sub || {}
    const out = {sub_code: s.sub_code}
    ;['sub_name', 'sub_description', 'sub_info'].forEach((b) => { out[b] = s[b]; out[b + '_e'] = s[b + '_e']; out[b + '_c'] = s[b + '_c'] })
    if (s.sub_data_header) out.sub_data_header = s.sub_data_header
    return out
}
